// BO'S REAL MAIL HISTORY — HubSpot email engagements turned into threads.
//
// WHY THIS FILE EXISTS. The cadence engine (lib/agentRun.js) used to read
// outbound history from the WORKSPACE ACTIVITY LOG — the `email_sent` events
// this app writes when it sends. This app never sends. So the log is empty,
// and "/follow-up" answered "No outbound send in the activity log" while Bo's
// real sends and his prospects' real replies sat in the HubSpot mirror the
// whole time. Bo sends from HubSpot; HubSpot is the system of record; this
// module is the one place that reads it as a send history.
//
// DIRECTION IS HUBSPOT -> APP, ALWAYS. Every read here goes through
// lib/hubspot.js, which has no write path at all (see its header). Nothing in
// this file sends, drafts, enrolls, or writes anything anywhere.
//
// WHAT IT REFUSES TO DO
//   - It never guesses that a send happened. An unreachable / forbidden mirror
//     resolves to { available:false, reason, threads: EMPTY } and the caller is
//     expected to ADMIT that, never to report zero touches. "We could not read
//     it" and "there is nothing" are different facts.
//   - It never counts a BOUNCED / FAILED / DRAFT / SCHEDULED email as a touch.
//     A bounce is not a conversation; a draft is not a send. Those are counted
//     separately (bouncedCount) so a caller can say so out loud.
//   - It never counts an auto-reply as a reply. Same AUTOREPLY_RE as
//     lib/cadence.js, so the reply freeze means the same thing on every surface.
//   - It never decides WHO WE ARE from a hardcoded address. Our addresses are
//     derived from the workspace: the signed-in user, plus every domain the
//     mirror actually shows sending outbound (and never shows writing IN to us).
//     Bo, Jacob and Jules are all "us" without this file naming any of them.
//   - classifyThread() makes NO model call and NO network call. It is a pure
//     heuristic over text we actually hold, and when the evidence is thin it
//     answers related:false rather than pushing a stranger's email at Bo.
//   - classifyThread() never lets OUR OWN WORDS decide that a thread is about
//     the product. Our signature carries the product's name in it, and a reply
//     quotes that signature back at us; see stripOurOwnWords() below.
//   - classifyThread() weighs INTERNAL OPS evidence (pay stubs, VPN,
//     QuickBooks, a docking station) against sales evidence instead of
//     keyword-matching either one. A real prospect thread in this portal is
//     titled "Revailo - Pricing and Laptop/Desktop requirements!", so "laptop"
//     cannot be a veto; twenty emails about laptops, pay stubs and a broken
//     contact form must not be a sale. See internalOpsVerdict() below.
import {
  hubspotEngagementPage,
  ENGAGEMENT_OUTBOUND,
  ENGAGEMENT_INBOUND,
  isDeliveredEmailStatus,
} from './hubspot.js'
import { AUTOREPLY_RE } from './cadence.js'
import { currentScope } from './userScope.js'

// One page of engagements. The read API caps `limit` at 200; asking for more
// silently gets 200, so this asks for exactly what it can have.
export const MAIL_PAGE_LIMIT = 200
// Hard bound on the walk. 40 * 200 = 8,000 engagements; the mirror holds
// ~3,100 on 2026-09-11 and gains ~200 a week, so this is years of runway, and
// a ceiling that cannot turn a paging bug into an infinite loop inside the run
// lock. It was 12 pages when the read API paged nothing at all (no cursor —
// one page of the 200 newest, which by September reached back a WEEK, so a
// company Bo had emailed four times read as touched once and the ladder said
// "Touch 2"); the API pages the engagement feed now, and this bound is what
// lets the walk actually reach the bottom of it.
export const MAIL_MAX_PAGES = 40
// What a card may show of a body. Long enough to recognise the thread, short
// enough that nobody mistakes it for the email.
export const SNIPPET_MAX = 160
// What THEY said gets a little more room — a rep answering by hand needs it.
export const REPLY_SNIPPET_MAX = 200

// ── addresses ───────────────────────────────────────────────────────────────

const ADDR_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi

// Every address in a raw header value ("Bo <bo@revailo.ai>, jules@revailo.ai"),
// lowercased. HubSpot's to/from props are sometimes a bare address and
// sometimes a display-name header; both parse here.
export function addressesIn(value) {
  const found = String(value || '').match(ADDR_RE)
  return found ? found.map((a) => a.toLowerCase()) : []
}

export function domainOf(address) {
  const a = String(address || '').trim().toLowerCase()
  const at = a.lastIndexOf('@')
  if (at === -1) return ''
  const d = a.slice(at + 1).trim()
  return d.includes('.') ? d : ''
}

// A LOCAL, deliberately small shared-host guard, used only to stop a signed-in
// user on a consumer mailbox from declaring gmail.com "our domain". The full
// list lives in agentRun.js (isFreemailDomain) and callers there pass it in —
// this default is the floor, not the ceiling.
const SHARED_MAIL_HOSTS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'live.com', 'aol.com', 'icloud.com', 'me.com', 'proton.me', 'protonmail.com',
  'msn.com', 'comcast.net', 'att.net', 'verizon.net', 'gmx.com',
])
const defaultSharedMailHost = (d) => SHARED_MAIL_HOSTS.has(String(d || '').toLowerCase())

// Sender local-parts that are machines, never a person to follow up with.
const ROBOT_LOCAL_RE =
  /^(no-?reply|do-?not-?reply|donotreply|mailer-daemon|postmaster|bounces?|notifications?|alerts?|automated|noreply-.*)$/i

function localPartOf(address) {
  const a = String(address || '').toLowerCase()
  const at = a.lastIndexOf('@')
  return at === -1 ? '' : a.slice(0, at)
}

export function isRobotAddress(address) {
  return ROBOT_LOCAL_RE.test(localPartOf(address))
}

// ── auto-responders ─────────────────────────────────────────────────────────
//
// cadence.js's AUTOREPLY_RE is ^-anchored on the SUBJECT, which catches
// "Automatic reply: …" and "Out of Office: …" and nothing else. It misses the
// two shapes the mirror actually hands us most often: a row whose subject was
// never captured (389 of the 2,066 engagements in the mirror carry no subject
// at all) and Gmail's vacation responder, which answers with a plain
// "Re: <original subject>". Both then read as a person writing back — they
// freeze the cadence and put an autoresponder in the hand-answer lane, the
// exact failure the auto-reply rule exists to prevent, entered through the body
// instead of the subject.
//
// So the body gets read too — but only its HEAD, and only for phrases an
// autoresponder OPENS with, so a signature, a postscript or a prospect
// mentioning their office cannot trip it.
const AUTOREPLY_BODY_HEAD = 300
const AUTOREPLY_BODY_RE = new RegExp(
  [
    'out of (?:the )?office',
    'away from (?:the|my) (?:office|desk|e-?mail)',
    'automatic(?:ally)? (?:repl|generat)',
    'auto[- ]?reply',
    'automated (?:response|reply)',
    'this is an automated',
    'on (?:vacation|holiday|annual leave|parental leave|maternity leave|paternity leave|sabbatical)\\b',
    '(?:will|shall) not be (?:checking|reading|monitoring)\\s+(?:my |our )?(?:e-?mail|messages|inbox)',
    '(?:no|limited) access to (?:my |our )?e-?mail',
    '(?:i am|i\'m|we are|we\'re) currently (?:out|away|unavailable)',
    'thank you for your (?:e-?mail|message)[.,!]?\\s*(?:i|we)\\s*(?:am|are)\\s*(?:currently )?(?:out|away)',
  ].join('|'),
  'i',
)

/**
 * Is this inbound engagement an autoresponder rather than a person?
 * Subject first (the same rule every other surface uses), then the head of the
 * body for the subject-less and "Re: …" responders the subject test cannot see.
 */
export function looksAutoReply(subject, body) {
  if (AUTOREPLY_RE.test(String(subject || '').trim())) return true
  const head = String(body || '')
    .replace(/<[^>]*>/g, ' ')
    .slice(0, AUTOREPLY_BODY_HEAD)
  return AUTOREPLY_BODY_RE.test(head)
}

// ── opt-outs ────────────────────────────────────────────────────────────────
//
// "Please remove all of us with Design Masonry from your contact list" is not
// a reply to answer — it is an instruction to stop, and the machine has to
// hear it. Found 2026-08-25: a company said exactly that, Bo acknowledged it,
// and the cadence still queued a follow-up to a COLLEAGUE of the person who
// asked, because nothing anywhere read the words inside an inbound email.
//
// Explicit stop/remove/unsubscribe language only. A soft "not interested
// right now" is a reply — the reply freeze already pulls those out of the
// cadence permanently — while this flag is the stronger fact ("they asked us
// to stop") that the exclusion node and the drafter must both see, quoted.
export const OPT_OUT_RE = new RegExp(
  [
    'remove (?:me|us|all of us|my (?:e-?mail|address)|our (?:e-?mails?|addresses))\\b',
    'take (?:me|us) off\\b',
    '(?:please |kindly )?unsubscribe(?: me| us)?\\b',
    'stop (?:e-?mailing|contacting|messaging) (?:me|us)\\b',
    'do not (?:e-?mail|contact) (?:me|us)\\b',
    "don'?t (?:e-?mail|contact) (?:me|us)\\b",
    'no longer (?:wish|want) to (?:receive|hear)',
    'off (?:your|the) (?:contact|mailing|e-?mail) list',
    'from your (?:contact|mailing|e-?mail) list',
  ].join('|'),
  'i',
)

// A reply QUOTES our own mail below it, and our mail may legitimately carry
// the word "unsubscribe" (a footer, or our own acknowledgment of an earlier
// opt-out). Only the part the PERSON typed may opt a thread out, so the body
// is cut at the first quoted-reply marker before the test runs. The markers
// are the shapes real clients emit — Outlook's "From:" header block, Gmail's
// "On … wrote:", divider runs — after tags are stripped.
const REPLY_QUOTE_MARKERS = /(?:^|\n)\s*(?:from\s*:|on .{0,120}? wrote\s*:|-{4,}|_{6,}|>{1})/i
const OPT_OUT_HEAD_MAX = 800

export function optOutIn(subject, body) {
  const text = String(body || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z#0-9]+;/gi, ' ')
  const cut = text.search(REPLY_QUOTE_MARKERS)
  const head = (cut === -1 ? text : text.slice(0, cut)).slice(0, OPT_OUT_HEAD_MAX)
  const scan = `${String(subject || '')}\n${head}`
  const hit = scan.match(OPT_OUT_RE)
  if (!hit) return null
  // The evidence, verbatim and short: the receipt quotes THEIR words, so a rep
  // reading "excluded — they asked us to stop" can check it against the mail.
  const at = Math.max(0, scan.indexOf(hit[0]) - 40)
  return { quote: scan.slice(at, at + hit[0].length + 80).replace(/\s+/g, ' ').trim() }
}

// ── time ────────────────────────────────────────────────────────────────────

// Touches dedup on the America/Chicago calendar day, exactly like the activity
// log's touch dedup in agentRun.js — so the two histories can be UNIONED
// without one source's touches double-counting the other's.
const CHI_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Chicago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export function chicagoDay(date) {
  try {
    return CHI_DAY_FMT.format(date)
  } catch {
    return ''
  }
}

// A mirror row may be stamped by a machine whose clock is ahead of ours, so a
// little future is drift, not garbage. A LOT of future is garbage.
const CLOCK_SKEW_MS = 24 * 60 * 60 * 1000

// Mirror timestamps are usually ISO and occasionally epoch milliseconds. Both
// parse; anything else is null and the engagement is counted as undatable
// rather than pinned to "now".
//
// A stamp beyond `notAfter` is refused for the same reason an unparseable one
// is: it is not a time this send can have happened at. Accepting it would make
// the row the thread's last touch, park a live lead behind the wait gate for as
// long as the garbled date lasts (businessDaysSince walks FORWARD and returns 0
// for a future `from`, so the gate never opens), and print a nonsense date on
// the receipt. Refused rows are counted as undatable and reported, never
// dropped in silence.
function parseAt(value, notAfter = Infinity) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const d = /^\d{10,}$/.test(raw) ? new Date(Number(raw)) : new Date(raw)
  const t = d.getTime()
  if (Number.isNaN(t)) return null
  if (t > notAfter) return null
  return d
}

// ── body text ───────────────────────────────────────────────────────────────

// A quoted reply block ("On Tue, Aug 4 … wrote:", "> like this") is the
// PREVIOUS message, not this one. Including it would let our own outbound copy
// leak into a snippet labelled as what THEY said.
const QUOTE_CUT_RE =
  /(^|\n)\s*(?:on\s.{0,120}?\bwrote:|-{2,}\s*original message|_{10,}|begin forwarded message|from:\s|sent from my )/i

// Tags whose CONTENT is markup rather than message. Stripping the tag alone
// leaves a stylesheet standing where the email should be, and on a real
// marketing-styled reply that CSS is the whole snippet.
const MARKUP_BLOCK_RE = /<(style|script|head|title)\b[^>]*>[\s\S]*?<\/\1\s*>/gi

// The entities a synced HTML body actually carries. Decoded AFTER tags are
// stripped, so an escaped "&lt;b&gt;" ends up as readable text instead of
// being mistaken for a tag and deleted.
const NAMED_ENTITIES = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  mdash: '—', ndash: '–', hellip: '…', laquo: '«', raquo: '»',
  rsquo: '’', lsquo: '‘', ldquo: '“', rdquo: '”', middot: '·', bull: '•',
  trade: '™', reg: '®', copy: '©', deg: '°', euro: '€', pound: '£', yen: '¥',
}

function decodeEntities(value) {
  return String(value).replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]{1,10});/gi, (whole, name) => {
    const key = name.toLowerCase()
    if (key[0] === '#') {
      const code = key[1] === 'x' ? parseInt(key.slice(2), 16) : parseInt(key.slice(1), 10)
      if (!Number.isFinite(code) || code < 9 || code > 0x10ffff) return whole
      try {
        return String.fromCodePoint(code)
      } catch {
        return whole
      }
    }
    // An entity we do not know stays exactly as written — inventing a
    // character for it would put words in the sender's mouth.
    return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, key) ? NAMED_ENTITIES[key] : whole
  })
}

export function snippetOf(text, max = SNIPPET_MAX) {
  let s = String(text || '')
  if (!s.trim()) return ''
  // HubSpot stores hs_email_text as plain text, but a synced HTML body can
  // carry markup and entities; resolve both before measuring length.
  s = s.replace(MARKUP_BLOCK_RE, ' ').replace(/<[^>]*>/g, ' ')
  s = decodeEntities(s)
  const cut = s.search(QUOTE_CUT_RE)
  // EVERYTHING from the quote marker on belongs to the PREVIOUS message —
  // which, on a reply, is OUR outreach copy. There is no length below which
  // that stops being true: "Sure, send it over." above our own pitch is the
  // commonest reply shape there is, and keeping the tail would print our words
  // inside curly quotes under "<name> replied", cited to HubSpot, as what THEY
  // said. When the quote is all we hold, the honest snippet is the empty one —
  // the card renders nothing rather than somebody else's sentence.
  if (cut >= 0) s = s.slice(0, cut)
  s = s
    .split('\n')
    .filter((line) => !/^\s*>/.test(line))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (s.length <= max) return s
  // Cut on a word boundary when there is one nearby, and never exceed `max`
  // INCLUDING the ellipsis — a "160 char max" that returns 163 is a lie the
  // card would render.
  const room = max - 1
  const head = s.slice(0, room)
  const space = head.lastIndexOf(' ')
  return `${(space > room - 20 ? head.slice(0, space) : head).trimEnd()}…`
}

// ── the read ────────────────────────────────────────────────────────────────

const emptyThreads = () => new Map()

function blankThread(email) {
  return {
    email,
    domain: domainOf(email),
    lastOutboundAt: '',
    lastInboundAt: '',
    lastAutoReplyAt: '',
    outboundCount: 0,
    inboundCount: 0,
    bouncedCount: 0,
    autoReplyCount: 0,
    // Deduped Chicago calendar days on which a DELIVERED outbound went out.
    // touches.size is the cadence's prior-touch count.
    touches: new Set(),
    lastOutbound: null,
    lastInbound: null,
    companyIds: [],
    // Bounded evidence for classifyThread — never rendered as an email.
    subjects: [],
    snippets: [],
    // THE THREAD ITSELF, dated: one entry per real mirrored message, so a card
    // can show "we wrote this, on this day, and this came back" instead of a
    // bare subject line. This is the difference between a rep trusting the
    // cadence number and wondering whether the model made it up — the entries
    // are copied from mirrored HubSpot engagements and nothing here is
    // generated. Bounces and auto-replies are KEPT and flagged rather than
    // dropped: "that one bounced" is exactly the kind of thing a rep needs to
    // see before sending the next touch.
    messages: [],
    // Which of our addresses actually wrote to them (bo@, jacob@, …).
    senders: new Set(),
    // {at, from, quote} when a HUMAN inbound asked us to stop — see optOutIn.
    optedOut: null,
  }
}

const EVIDENCE_MAX = 12

function pushEvidence(list, value) {
  const v = String(value || '').trim()
  if (!v || list.length >= EVIDENCE_MAX) return
  if (!list.includes(v)) list.push(v)
}

// How many dated messages a thread keeps. The mirror hands them over in
// whichever order the feed paged, so the list is collected loosely here and
// sorted newest-first when the view is built; this cap is the collection
// ceiling, not the render count.
const THREAD_MESSAGE_MAX = 40

function pushMessage(thread, entry) {
  if (!entry || !entry.at) return
  if (thread.messages.length >= THREAD_MESSAGE_MAX) return
  // One mirrored engagement can reach the same thread twice (two recipients at
  // one company); its id makes that one message, not two.
  if (entry.id && thread.messages.some((m) => m.id === entry.id)) return
  thread.messages.push(entry)
}

function laterIso(current, date) {
  if (!date) return current
  const iso = date.toISOString()
  return !current || iso > current ? iso : current
}

/**
 * Read Bo's outbound/inbound email history out of the HubSpot mirror.
 *
 * @param {object}   [opts]
 * @param {AbortSignal} [opts.signal]  aborts BETWEEN pages (throws AbortError)
 * @param {string}   [opts.selfEmail]  the signed-in user; defaults to the
 *                                     workspace scope (lib/userScope.js)
 * @param {string}   [opts.since]      ISO lower bound handed to the API
 * @param {Date|number} [opts.now]     the run's clock; stamps beyond it (plus a
 *                                     day of skew) are refused as undatable
 * @param {number}   [opts.maxPages]   bound on the walk (default MAIL_MAX_PAGES)
 * @param {function} [opts.sharedMailHost]  (domain) => boolean; callers pass
 *                                     agentRun's fuller isFreemailDomain
 * @param {function} [opts.readPage]   test seam: (params) => page
 *
 * @returns {Promise<{available:boolean, reason:string, threads:Map<string,object>,
 *   ourAddresses:Set<string>, ourDomains:Set<string>, pagesRead:number,
 *   engagementsRead:number, outboundRead:number, inboundRead:number,
 *   undelivered:number, undatable:number, futureDated:number,
 *   unattributed:number, unattributedSender:number, complete:boolean,
 *   latestAt:string}>}
 */
export async function readMailHistory({
  signal,
  selfEmail,
  since = '',
  now = undefined,
  maxPages = MAIL_MAX_PAGES,
  pageLimit = MAIL_PAGE_LIMIT,
  sharedMailHost = defaultSharedMailHost,
  readPage = hubspotEngagementPage,
} = {}) {
  const fail = (reason) => ({
    available: false,
    reason: reason || 'The HubSpot mirror could not be read.',
    threads: emptyThreads(),
    ourAddresses: new Set(),
    ourDomains: new Set(),
    pagesRead: 0,
    engagementsRead: 0,
    outboundRead: 0,
    inboundRead: 0,
    undelivered: 0,
    undatable: 0,
    futureDated: 0,
    unattributed: 0,
    unattributedSender: 0,
    complete: false,
    latestAt: '',
  })

  // ── 1. walk the pages ────────────────────────────────────────────────────
  const rows = []
  let pagesRead = 0
  let complete = true
  let partialReason = ''
  let cursor = ''
  for (let page = 0; page < Math.max(1, maxPages); page++) {
    if (signal?.aborted) throw abortError()
    let res = null
    try {
      res = await readPage({
        limit: pageLimit,
        ...(since ? { since } : {}),
        ...(cursor ? { cursor } : {}),
      })
    } catch (err) {
      res = { available: false, reason: err?.message || '' }
    }
    if (!res || res.available !== true) {
      // A FIRST page that will not answer is the whole read failing — there is
      // nothing honest to say except that we could not ask.
      if (page === 0) return fail(res?.reason)
      complete = false
      partialReason = res?.reason || 'a later page of engagements did not answer'
      break
    }
    pagesRead += 1
    const batch = Array.isArray(res.engagements) ? res.engagements : []
    rows.push(...batch)
    cursor = String(res.cursor || '')
    // No cursor means the API gave us everything it will give us in one go. A
    // full page with no cursor is NOT provably complete, and says so.
    if (!cursor) {
      if (batch.length >= pageLimit) {
        complete = false
        partialReason = `the mirror returned a full page of ${batch.length} engagements and no cursor, so older ones were not read`
      }
      break
    }
    if (page === Math.max(1, maxPages) - 1) {
      complete = false
      partialReason = `stopped at the ${maxPages}-page bound with more engagements still to read`
    }
  }

  // ── 2. who is US ─────────────────────────────────────────────────────────
  // Derived, never hardcoded — and WEIGHED, never vetoed. Every engagement puts
  // each domain it names on one side of the mail:
  //   OURS   — the SENDER of an outbound row, and every RECIPIENT of an inbound
  //            row (a prospect writing in is writing to us)
  //   THEIRS — every RECIPIENT of an outbound row, and the SENDER of an
  //            inbound row
  // A domain landing on both sides of the SAME row — an internal CC, a
  // colleague's reply-all, a forward, a BCC-to-CRM — is credited only to the
  // OURS side of that row, because that is what such a row means.
  //
  // WHY IT IS COUNTED AND NOT VETOED. This used to read "outbound sender at
  // least once AND never an inbound sender", so ONE internal forward from our
  // own domain disqualified it, and with it every send Bo ever made and every
  // reply he ever got — silently, reported as zero rather than admitted. The
  // live mirror holds 670 inbound rows and 749 of Bo's sends are @revailo.ai
  // while this workspace signs in on @revailo.com, so that veto only ever held
  // by luck. The same line failed inverted too: an email LOGGED into HubSpot by
  // hand arrives as direction=EMAIL from the PROSPECT, which made the
  // prospect's domain "ours" and erased their real thread.
  //
  // A domain is ours when the evidence for it outweighs the evidence against,
  // plus the signed-in user's own domain by definition — unless it is a shared
  // consumer host, which is nobody's company. This is still how
  // jacob@revailo.ai and jules@revailo.ai are "us" without naming a person.
  const domainSides = new Map()
  const sideOf = (d) => {
    let v = domainSides.get(d)
    if (!v) {
      v = { ours: 0, theirs: 0 }
      domainSides.set(d, v)
    }
    return v
  }
  for (const raw of rows) {
    const outbound = raw?.direction === ENGAGEMENT_OUTBOUND
    const inbound = raw?.direction === ENGAGEMENT_INBOUND
    if (!outbound && !inbound) continue
    const fromDomain = domainOf(addressesIn(raw?.from)[0] || '')
    const toDomains = new Set(addressesIn(raw?.to).map(domainOf).filter(Boolean))
    const oursHere = new Set()
    const theirsHere = new Set()
    if (outbound) {
      if (fromDomain) oursHere.add(fromDomain)
      for (const d of toDomains) theirsHere.add(d)
    } else {
      if (fromDomain) theirsHere.add(fromDomain)
      for (const d of toDomains) oursHere.add(d)
    }
    for (const d of oursHere) {
      sideOf(d).ours += 1
      theirsHere.delete(d)
    }
    for (const d of theirsHere) sideOf(d).theirs += 1
  }
  const self = String(selfEmail ?? currentScope() ?? '').trim().toLowerCase()
  const selfAddr = self.includes('@') ? self : ''
  const ourDomains = new Set()
  const selfDomain = domainOf(selfAddr)
  if (selfDomain && !sharedMailHost(selfDomain)) ourDomains.add(selfDomain)
  for (const [d, tally] of domainSides) {
    if (sharedMailHost(d)) continue
    if (tally.ours > tally.theirs) ourDomains.add(d)
  }
  const ourAddresses = new Set()
  if (selfAddr) ourAddresses.add(selfAddr)
  for (const raw of rows) {
    if (raw?.direction !== ENGAGEMENT_OUTBOUND) continue
    const from = addressesIn(raw?.from)[0] || ''
    if (from && ourDomains.has(domainOf(from))) ourAddresses.add(from)
  }
  const isOurs = (addr) => {
    const a = String(addr || '').toLowerCase()
    if (!a) return false
    return ourAddresses.has(a) || ourDomains.has(domainOf(a))
  }

  // ── 3. fold engagements into threads ─────────────────────────────────────
  const threads = emptyThreads()
  let outboundRead = 0
  let inboundRead = 0
  let undelivered = 0
  let undatable = 0
  let futureDated = 0
  let unattributed = 0
  let unattributedSender = 0
  let latestAt = ''

  const threadFor = (addr) => {
    let t = threads.get(addr)
    if (!t) {
      t = blankThread(addr)
      threads.set(addr, t)
    }
    return t
  }
  const mergeCompanies = (t, ids) => {
    for (const id of Array.isArray(ids) ? ids : []) {
      const v = String(id || '').trim()
      if (v && !t.companyIds.includes(v)) t.companyIds.push(v)
    }
  }

  // Oldest first, so "last" always means last.
  const notAfter = nowMs(now) + CLOCK_SKEW_MS
  const dated = []
  for (const raw of rows) {
    const at = parseAt(raw?.at, notAfter)
    if (!at) {
      // A send with no readable timestamp — or one stamped in the future, which
      // is the same thing — cannot be wait-gated, so it is counted and reported
      // rather than dropped silently or dated to now.
      undatable += 1
      if (parseAt(raw?.at)) futureDated += 1
      continue
    }
    dated.push({ raw, at })
  }
  dated.sort((a, b) => a.at - b.at)

  for (const { raw, at } of dated) {
    const subject = String(raw?.subject || '').trim()
    const body = String(raw?.body || '')
    const iso = at.toISOString()
    if (!latestAt || iso > latestAt) latestAt = iso

    if (raw.direction === ENGAGEMENT_OUTBOUND) {
      const from = addressesIn(raw.from)[0] || ''
      // HubSpot's own direction says outbound; we still require the sender to
      // be one of OURS. A mis-synced row from a stranger's address is not our
      // send, and counting it would invent a touch Bo never made — and so is a
      // row with NO sender at all, which is exactly what the old `from &&`
      // short-circuit let through: it advanced the cadence ladder off a record
      // nobody could attribute, with `senders` empty so the receipt could not
      // even name who sent it. Refused rows are COUNTED, so the receipt says
      // "we saw N we could not attribute" instead of quietly reporting fewer
      // touches than the mirror holds.
      if (!from || !isOurs(from)) {
        unattributedSender += 1
        continue
      }
      const counterparts = addressesIn(raw.to).filter((a) => a && !isOurs(a) && !isRobotAddress(a))
      if (!counterparts.length) {
        unattributed += 1
        continue
      }
      const delivered = isDeliveredEmailStatus(raw.status)
      if (delivered) outboundRead += 1
      else undelivered += 1
      for (const addr of counterparts) {
        const t = threadFor(addr)
        if (from) t.senders.add(from)
        mergeCompanies(t, raw.companyIds)
        pushEvidence(t.subjects, subject)
        pushEvidence(t.snippets, snippetOf(body))
        // On the thread whether or not it landed — a rep reading the history
        // needs to see the bounce, and the flag says which it was.
        pushMessage(t, {
          id: String(raw.id || ''),
          dir: 'out',
          subject,
          snippet: snippetOf(body),
          at: iso,
          from,
          delivered,
        })
        if (!delivered) {
          // BOUNCED / FAILED / DRAFT / SCHEDULED: never a touch, never a
          // lastOutboundAt. A bounce that started the wait gate would park a
          // live lead for three business days on a message nobody received.
          t.bouncedCount += 1
          continue
        }
        t.outboundCount += 1
        t.touches.add(chicagoDay(at))
        if (!t.lastOutboundAt || iso > t.lastOutboundAt) {
          t.lastOutboundAt = iso
          t.lastOutbound = { subject, snippet: snippetOf(body), at: iso }
        }
      }
      continue
    }

    if (raw.direction === ENGAGEMENT_INBOUND) {
      const from = addressesIn(raw.from)[0] || ''
      // Our own address on an INCOMING row is a mirror artefact, not a reply
      // from a prospect. Robots (mailer-daemon, no-reply) are not people.
      if (!from || isOurs(from) || isRobotAddress(from)) continue
      const t = threadFor(from)
      mergeCompanies(t, raw.companyIds)
      pushEvidence(t.subjects, subject)
      pushEvidence(t.snippets, snippetOf(body))
      // Same shapes lib/cadence.js refuses to call a reply, plus the body test
      // for the responders that carry no subject or answer with a plain
      // "Re: …". An out-of-office must NOT freeze a cadence, and must never be
      // handed to a rep as a person waiting on an answer — that is the bug this
      // rule exists for.
      const auto = looksAutoReply(subject, body)
      // Auto-replies ride on the thread too, flagged: a rep looking at the
      // history should see the out-of-office that came back, and see that the
      // cadence correctly did not treat it as an answer.
      pushMessage(t, {
        id: String(raw.id || ''),
        dir: 'in',
        subject,
        snippet: snippetOf(body, REPLY_SNIPPET_MAX),
        at: iso,
        from,
        auto,
      })
      if (auto) {
        t.autoReplyCount += 1
        t.lastAutoReplyAt = laterIso(t.lastAutoReplyAt, at)
        continue
      }
      // A human asking to be removed is the loudest thing a thread can say.
      // Detected here, on the human-typed head of the reply only (see
      // optOutIn), and kept with their words so every surface downstream can
      // quote the request instead of asserting it.
      const optOut = optOutIn(subject, body)
      if (optOut && (!t.optedOut || iso > t.optedOut.at)) {
        t.optedOut = { at: iso, from, quote: optOut.quote }
      }
      inboundRead += 1
      t.inboundCount += 1
      if (!t.lastInboundAt || iso > t.lastInboundAt) {
        t.lastInboundAt = iso
        t.lastInbound = {
          subject,
          snippet: snippetOf(body, REPLY_SNIPPET_MAX),
          at: iso,
        }
      }
      continue
    }
    // Any other engagement kind (call, note, meeting) is not mail history.
  }

  return {
    available: true,
    reason: partialReason,
    threads,
    ourAddresses,
    ourDomains,
    pagesRead,
    engagementsRead: rows.length,
    outboundRead,
    inboundRead,
    undelivered,
    undatable,
    futureDated,
    unattributed,
    unattributedSender,
    complete,
    latestAt,
  }
}

// The run's clock as milliseconds. A caller that hands us the run's `now` gets
// a deterministic read; one that does not falls back to the wall clock.
function nowMs(value) {
  if (value instanceof Date) {
    const t = value.getTime()
    if (!Number.isNaN(t)) return t
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  return Date.now()
}

function abortError() {
  const err = new Error('Run cancelled')
  err.name = 'AbortError'
  return err
}

// ── classification ──────────────────────────────────────────────────────────
//
// ONLY threads plausibly about software / SaaS / sales / outreach / estimating
// are followable. This is a PURE HEURISTIC over text we actually hold — the
// subjects and body snippets already on the thread — and it makes no model call
// and no network call. It is conservative in BOTH directions: it never invents a
// topic, and thin evidence produces related:false with "not clearly about the
// product" rather than pushing a stranger's email at Bo to answer.

// A hit on any of these is real product/sales evidence. Word-boundaried so
// "bid" does not match "forbidden" and "trial" does not match "industrial".
//
// "QUOTE" IS NOT ON THIS LIST ANY MORE, and one word is the whole reason. The
// Chicago Masonry thread — twenty emails of IT and payroll between colleagues —
// carried the line "when you are on the website and send a message for a quote
// we are not getting them": a coworker reporting that THEIR OWN contact form
// had stopped delivering. That single word qualified the entire conversation as
// a sales thread and earned a cold software pitch to our own teammate. A broken
// quote FORM is not a quote. The word moved to WEAK_RE, so it still appears in
// the refusal reason ("only 'quote' — no estimating, takeoff, demo or pricing
// wording…") and a rep reading the receipt can see exactly what we found and
// overrule us — it simply can no longer carry a thread by itself. What that
// costs: a genuine "can you send me a quote?" that names nothing else. Rare,
// because such a mail almost always says pricing, proposal or takeoff in the
// same breath, and cheap when it happens — one missed follow-up, against a
// pitch to a colleague.
//
// THE LIST COMES IN TWO STRENGTHS, and the split exists for one reason: not
// every word on it implies a sale. "Takeoffs", "estimating", "pricing",
// "Revailo" are words a masonry contractor writes to a VENDOR and almost
// nowhere else. "Software", "platform", "integration", "onboarding", "license"
// are the everyday nouns of an internal IT thread — the new hire is being
// onboarded, the software on the laptop needs a license, the QuickBooks
// integration is down — and treating them as sales evidence is how the original
// bug walked back in through a different door. Both halves are the SAME
// classifier for the topic gate (PRODUCT_RE is their union, unchanged); the
// strength is used only where the two kinds of evidence are weighed against
// each other, in internalOpsVerdict().
const PRODUCT_STRONG_TERMS = [
  'revailo',
  'estimat(?:e|es|ing|or|ors|ion)',
  'take-?offs?',
  'quantity survey',
  'bid(?:s|ding)?',
  'proposals?',
  'demos?',
  'walkthroughs?',
  'free trial',
  'trials?',
  'pricing',
  'price list',
  'seats?',
  'plan sets?',
  'blueprints?',
  'sales sequence',
  'follow[- ]?up sequence',
]

// Generic SaaS vocabulary. Real sales words — a genuine deal says them — but
// they are also what a colleague writes about the accounting package, so on
// their own they are doubt, not evidence.
const PRODUCT_GENERIC_TERMS = [
  'licen[cs]e(?:s|d|ing)?',
  'subscriptions?',
  'software',
  'platform',
  'saas',
  'onboarding',
  'implementation',
  'integration',
]

const PRODUCT_TERMS = [...PRODUCT_STRONG_TERMS, ...PRODUCT_GENERIC_TERMS]

const PRODUCT_RE = new RegExp('\\b(' + PRODUCT_TERMS.join('|') + ')\\b', 'i')

// Anchored, so a term already matched out of the text can be sorted into its
// strength. Not used to find anything — only to ask "which kind was this one".
const PRODUCT_GENERIC_ONLY_RE = new RegExp('^(' + PRODUCT_GENERIC_TERMS.join('|') + ')$', 'i')

// The same words, global, used ONLY to count how much distinct sales evidence a
// thread holds (see internalOpsVerdict). Kept separate because a /g regex
// carries lastIndex between calls and .test() on one is a well-known way to get
// alternating true/false answers; this one is only ever fed to matchAll().
const PRODUCT_RE_G = new RegExp('\\b(' + PRODUCT_TERMS.join('|') + ')\\b', 'gi')

// Weak, on their own meaningless: a "quick call" could be anything, and a
// "quote" is as likely to be their broken web form as our price (see the
// PRODUCT_TERMS note). Counted for the REASON string, never enough on their own
// to say related:true.
const WEAK_RE =
  /\b(meeting|call|chat|intro|introduction|connect|schedule|availability|calendar|question|questions|quotes?)\b/i

// A hit here disqualifies the thread outright, however many product words it
// also carries — a recruiter's pitch is full of "software" and "platform".
const UNRELATED_RULES = [
  {
    id: 'recruiting',
    re: /\b(resum[eé]|curriculum vitae|\bcv\b|job (?:opening|opportunit(?:y|ies)|application|posting)|applying for|candidates?|recruit(?:er|ing|ment)|staffing agency|hiring for|open roles?)\b/i,
    why: 'it reads as recruiting, not a sales thread',
  },
  {
    id: 'newsletter',
    re: /\b(unsubscribe|newsletter|mailing list|view (?:this|it) in your browser|manage (?:your )?preferences|digest|press release)\b/i,
    why: 'it reads as a newsletter or bulk mail, not a conversation',
  },
  {
    id: 'vendor',
    re: /\b(invoice|receipt for|your order|order (?:confirmation|#)|shipping (?:update|confirmation)|tracking number|statement is (?:ready|available)|payment (?:due|received|failed)|billing statement|past due)\b/i,
    why: 'it reads as a vendor invoice or order notice, not our sales thread',
  },
  {
    id: 'account-security',
    re: /\b(password reset|reset your password|verify your (?:email|account)|verification code|one[- ]time (?:code|passcode)|\botp\b|two[- ]factor|security alert|suspicious (?:sign|log)-?in|account (?:locked|suspended))\b/i,
    why: 'it reads as an account or security notice, not a person writing to us',
  },
  {
    id: 'support-ticket',
    re: /\b(ticket #|case #|support request|incident \d|helpdesk|service request)\b/i,
    why: 'it reads as an unrelated support ticket',
  },
  {
    id: 'personal',
    re: /\b(happy birthday|congratulations on your|wedding|funeral|vacation photos|dinner (?:tonight|plans)|see you at (?:the )?(?:game|party))\b/i,
    why: 'it reads as personal mail',
  },
]

// ── internal ops: the rule that has to be precise, not loud ─────────────────
//
// THE BUG THIS FIXES. Twenty emails with chicagomasonry.com — our own sister
// company — about laptops, a docking station reimbursement, VPN access to
// company files and QuickBooks, pay stubs on the employee portal, and their
// website being down, classified as a sales conversation and earned a cold
// software pitch addressed to jacob@, our own teammate. Subjects: "Xu and
// Khushboo", "Xu's renewal", "laptops", "Laptop", "Ordered Dock", "Fulton May",
// "Pay Stubs", "CMC Website", "Verizon". There is no sale anywhere in it.
//
// WHY THIS RULE IS SCORED WHEN EVERY RULE ABOVE IS A FIRST-MATCH VETO. The
// rules in UNRELATED_RULES can veto on one hit because their words are
// unambiguous — nobody buying estimating software writes "unsubscribe" or
// "resumé" at us. The internal-ops vocabulary is NOT unambiguous, and the
// counter-example is sitting in this very portal: the best real sales thread
// we have is titled
//
//     "Revailo - Pricing and Laptop/Desktop requirements!"
//
// — a prospect asking what hardware the product needs. A first-match rule on
// `laptops?` would kill that conversation, which is a worse failure than the
// one we are fixing. So this rule weighs the WHOLE thread instead of stopping
// at the first word it recognises:
//
//   * STRONG ops words score 2. These are things nobody discussing masonry
//     takeoff software writes to a vendor: pay stubs, payroll, reimbursement,
//     expense report, docking station, VPN, QuickBooks, employee portal,
//     timesheet, W-2, benefits enrollment.
//   * WEAK ops words score 1. Each has an honest sales reading — laptops and
//     Office 365 are system-requirement questions, hosting and DNS are things a
//     SaaS buyer asks about, a password is half of a trial login, and a site
//     being down is as likely to be a vendor's outage as a colleague's. On
//     their own they decide nothing.
//   * PRODUCT WORDS PUSH BACK, BY HOW MUCH THEY ACTUALLY IMPLY A SALE. A
//     distinct STRONG product term (Revailo, takeoffs, pricing, seats, bids —
//     PRODUCT_STRONG_TERMS) is worth 2, the same as a pay stub, because a
//     masonry contractor writes those words to a vendor and almost nowhere
//     else. The GENERIC SaaS nouns (software, platform, integration,
//     onboarding, license — PRODUCT_GENERIC_TERMS) are worth ONE POINT
//     BETWEEN THEM, however many of them appear.
//
//     WHY THEY ARE CAPPED AS A GROUP RATHER THAN COUNTED EACH. They travel in
//     packs in exactly the mail this rule is for: "the quickbooks integration
//     on the new laptop, the software platform is down" is three of them in one
//     sentence of an IT ticket, and counting them independently turned one weak
//     signal into six points of "sales evidence" that outvoted a pay stub and a
//     VPN. One point is what they are worth together: enough to be doubt,
//     never enough to carry a thread.
//
//     So "we'll need VPN access for the install — here's the pricing" stays a
//     sales thread (2 ops vs 4 sales), "laptops, VPN, pay stubs, employee
//     portal, software rollout" does not (7 vs 1), and the QuickBooks-on-the-
//     new-laptop ticket does not either (3 vs 1).
//
//   * A TIE REFUSES. The verdict fires when the ops evidence is at least as
//     strong as the sales evidence, not only when it wins outright — the
//     direction this whole file's "when in doubt, refuse" rule points. A missed
//     follow-up costs a rep one look; a pitch to a colleague costs trust.
//
// The floor is OPS_MIN_SCORE: one strong word, or two weak ones. A lone
// "laptop" never fires this rule, and never should.
//
// WHICH SIDE READS WHICH TEXT, AND WHY IT IS ASYMMETRIC. Ops words are counted
// in the thread exactly as it arrived — nothing we sign our mail with is a pay
// stub, so there is nothing of ours to strip. Product words are counted in the
// text stripOurOwnWords() has already cleaned of our own letterhead. That
// asymmetry is load-bearing: an internal thread forwarded around the office
// carries OUR signature with the product's name in it, and "Revailo" in our own
// footer must never out-vote their pay stubs.
//
// WHAT THIS STILL CANNOT DO. It reads words, not relationships. A colleague
// thread with no ops vocabulary at all ("Xu and Khushboo", "Verizon") is
// invisible to it; those threads are caught here only because the same
// conversation also mentions the docking station and the VPN. The relationship
// test lives in internalCounterpartWhy() below, and is honest about its own
// limits.
const OPS_STRONG_WEIGHT = 2
const OPS_WEAK_WEIGHT = 1
// Per distinct STRONG product term. Generic SaaS nouns share a single point
// between them (SALES_GENERIC_TOTAL) — see the note above on why they are
// capped as a group rather than counted one by one.
const SALES_TERM_WEIGHT = 2
const SALES_GENERIC_TOTAL = 1
const OPS_MIN_SCORE = 2

const OPS_STRONG_RES = [
  /\bpay ?stubs?\b/i,
  /\bpayroll\b/i,
  /\breimburse(?:ment|ments|d|s)?\b/i,
  /\bexpense reports?\b/i,
  /\bdocking stations?\b/i,
  /\bvpn\b/i,
  /\bquick ?books\b/i,
  /\bemployee portal\b/i,
  /\btime ?sheets?\b/i,
  /\bw-?2s?\b/i,
  /\bbenefits enroll(?:ment)?\b/i,
]

const OPS_WEAK_RES = [
  /\blap ?tops?\b/i,
  /\bpasswords?\b/i,
  /\bhosting\b/i,
  /\bdns\b/i,
  /\b(?:web)?site is down\b/i,
  /\boffice 365\b/i,
  /\bprinters?\b/i,
]

/**
 * Weigh internal-ops evidence against sales evidence.
 *
 * @param {string} rawText    the thread as it arrived (ops words are counted here)
 * @param {string} cleanText  the same thread with OUR letterhead blanked
 *   (product words are counted here — see the asymmetry note above)
 * @returns {{why:string, evidence:string, score:number, sales:number}|null}
 *   null when the thread is not internal ops, or when the sales evidence in it
 *   OUTWEIGHS the ops evidence. A tie is not null: it fires.
 */
function internalOpsVerdict(rawText, cleanText) {
  const raw = String(rawText || '')
  const hits = []
  let score = 0
  for (const re of OPS_STRONG_RES) {
    const m = raw.match(re)
    if (!m) continue
    score += OPS_STRONG_WEIGHT
    hits.push(m[0])
  }
  for (const re of OPS_WEAK_RES) {
    const m = raw.match(re)
    if (!m) continue
    score += OPS_WEAK_WEIGHT
    hits.push(m[0])
  }
  if (score < OPS_MIN_SCORE) return null

  // Distinct product terms, in THEIR words. Deduped on the matched spelling, so
  // "estimating" and "estimator" count twice — a small over-count, and it errs
  // toward letting a thread stay a sales thread, which is the direction a
  // TOPIC gate should err in. The refusal that protects a colleague from a
  // pitch is internalCounterpartWhy() and the model's own refusal rule, not
  // this arithmetic.
  const terms = new Set()
  for (const m of String(cleanText || '').matchAll(PRODUCT_RE_G)) terms.add(m[0].toLowerCase())
  // Sorted into the two strengths. The generic ones are worth one point
  // BETWEEN THEM — an IT ticket says "software", "platform" and "integration"
  // in one breath, and counting those three as six points of sales evidence is
  // what let ordinary internal mail out-vote a pay stub.
  let strong = 0
  let generic = 0
  for (const t of terms) {
    if (PRODUCT_GENERIC_ONLY_RE.test(t)) generic += 1
    else strong += 1
  }
  const sales = strong * SALES_TERM_WEIGHT + (generic ? SALES_GENERIC_TOTAL : 0)
  // `<` and not `<=`: a genuine tie REFUSES. See the note above.
  if (score < sales) return null

  const quoted = hits.slice(0, 3).map((h) => `“${String(h).slice(0, 40)}”`).join(', ')
  // When there WAS product wording and we overruled it, say so. "We saw the
  // word and weighed it" is checkable against the mail; a bare refusal looks
  // like a miss.
  // A TIE IS NOT A WIN, and the reason does not claim one. "Outweighed" would
  // be a small lie on the case where the two sides scored the same and we
  // refused on the tie-break; a rep who checks the mail would find the product
  // word standing right there and stop trusting the sentence.
  const outweighed = terms.size
    ? score > sales
      ? ` — the product wording in it (“${[...terms][0].slice(0, 40)}”) is outweighed`
      : ` — the product wording in it (“${[...terms][0].slice(0, 40)}”) only matches it, and a tie is refused`
    : ''
  return {
    why: `it reads as internal IT, payroll or office admin, not a sales thread (${quoted})${outweighed}`,
    evidence: hits[0],
    score,
    sales,
  }
}

// ── is the counterpart one of us? ───────────────────────────────────────────
//
// A mailbox name that is a ROLE, not a person. Two companies both having an
// info@ or an office@ says nothing at all about whether the same human is
// behind them, so those never match on the local-part test below.
const ROLE_LOCAL_RE =
  /^(info|sales|contact|admin|administrator|office|hello|team|support|help|billing|invoices?|payments?|accounting|accounts|hr|jobs|careers|marketing|service|services|estimating|estimator|orders?|mail|email|inbox)$/i

// How distinctive a mailbox name has to be before sharing it means anything.
// "bo" and "jw" collide by accident; "jacob" and "khushboo" do not.
const SHARED_LOCAL_MIN = 4

/**
 * Is the thread's counterpart one of OUR OWN people rather than a prospect?
 *
 * Three tests, in order of how sure we are:
 *   1. the address IS one of ours. readMailHistory already refuses to open a
 *      thread on one of our addresses, so this should be unreachable from that
 *      path — but classifyThread is exported, agentRun merges threads from
 *      three sources, and "ours" is derived by WEIGHING domains, which can be
 *      wrong on a thin mirror. The check costs one Set lookup and closes the
 *      hole for every caller instead of for one.
 *   2. the address is on a domain we own — a colleague on our own letterhead.
 *   3. the address shares a distinctive mailbox name with one of our people —
 *      the "same human, second mailbox" shape (jacob@revailo.ai and
 *      jacob@somewhere-else.com). Guarded by SHARED_LOCAL_MIN and
 *      ROLE_LOCAL_RE so it cannot fire on info@ or on two-letter initials.
 *
 * WHAT IT CANNOT CATCH, out loud, because pretending otherwise is how the
 * original bug shipped: the actual address in the actual bug.
 * jacob@revailo.ai and jwillner@chicagomasonry.com are the same human, and
 * NOTHING here can tell — different mailbox name, different company, and the
 * only thing connecting them is a fact about a person that lives in somebody's
 * head, not in the mirror. Catching it needs a roster of our people's alternate
 * addresses, which this module does not have and will not invent. That thread
 * is refused by internalOpsVerdict() on what the mail SAYS, and by the drafter's
 * own refusal rule; this function is the cheap half, not the whole answer.
 *
 * The known cost of test 3: a genuine prospect who happens to share a first
 * name with one of us ("jacob@somemasonry.com") is refused as internal and a
 * rep loses one follow-up. We take that trade knowingly — a missed follow-up
 * costs a look, a pitch to a colleague costs trust.
 *
 * @returns {{why:string, evidence:string}|null}
 */
function internalCounterpartWhy(email, who) {
  const addr = String(email || '').trim().toLowerCase()
  if (!addr.includes('@')) return null

  if (who.addresses.has(addr)) {
    return {
      why: `${addr} is one of our own addresses, so this thread is us writing to ourselves, not a prospect`,
      evidence: addr,
    }
  }

  const domain = domainOf(addr)
  if (domain && who.domains.has(domain)) {
    return {
      why: `${addr} is on ${domain}, one of our own domains — a colleague, not a prospect`,
      evidence: addr,
    }
  }

  const local = localPartOf(addr)
  if (local.length < SHARED_LOCAL_MIN || ROLE_LOCAL_RE.test(local)) return null
  for (const ourAddr of who.addresses) {
    const ourLocal = localPartOf(ourAddr)
    if (ourLocal !== local || ROLE_LOCAL_RE.test(ourLocal)) continue
    return {
      why: `${addr} shares a mailbox name with our own ${ourAddr}, so it reads as one of our people writing from a second address rather than a prospect`,
      evidence: addr,
    }
  }
  return null
}

// ── our own words are not their topic ───────────────────────────────────────
//
// THE BUG THIS FIXES. PRODUCT_RE lists "revailo", because a thread that names
// the product is a thread about the product. Our own signature names it too —
// every message we send ends with
//
//     Jacob Willner / Revailo / 3440 S. Dearborn St / Jacob@revailo.com
//
// and anything quoted, forwarded or replied-to carries that block along with
// it. snippetOf() cuts at QUOTE_CUT_RE, which catches "On … wrote:", "From: ",
// "-----Original Message" and friends — but a chain pasted without a
// recognised marker keeps the whole block, and a SUBJECT line was never cut at
// all. So an internal ops thread about laptops and pay stubs, forwarded around
// with our own footer attached, classified as "about the product — 'revailo'
// appears in the body" and earned a sales follow-up. The product word came
// from US, not from them.
//
// WHAT IS BLANKED, and nothing else:
//   1. every email address of ours — an exact address we know, or any address
//      at a domain we own. "jacob@revailo.com" contains the standalone word
//      "revailo" and PRODUCT_RE matches it happily.
//   2. every bare mention of a domain we own ("revailo.com", "revailo.ai").
//   3. our own brand name when it sits INSIDE our own signature block — that
//      is, within OUR_SIG_NEAR characters of one of our addresses, which is
//      exactly what a signature is: name, company, street, address, all in one
//      short run.
//
// WHAT IS DELIBERATELY NOT BLANKED. Our outbound copy at large. Our own pitch
// is real evidence that a thread is about the product — on a cold thread it is
// the ONLY text there is, and neutralising it would classify every first-touch
// thread "not clearly about the product" and empty the entire cadence. Only
// the identifiers and the footer are ours-rather-than-theirs; the words of the
// message stand, whoever typed them. Everything this deletes is a character
// the topic gate can no longer find THEIR words in, so it deletes as little as
// it can get away with.
//
// The UNRELATED_RULES are NOT run against the blanked text — they still read
// the thread exactly as it arrived, so nothing about this weakens them.

// How close to one of our addresses our brand name has to sit before it counts
// as part of our signature rather than as somebody's topic. Measured on the
// real footer: "Revailo 3440 S. Dearborn St Chicago IL 60616 Jacob@revailo.com"
// is 38 characters from the brand to the address. 60 covers it with a little
// room and stops well short of a sentence somebody wrote.
const OUR_SIG_NEAR = 60

const escapeRe = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function lowerSet(values) {
  const out = new Set()
  for (const v of values || []) {
    const s = String(v || '').trim().toLowerCase()
    if (s) out.add(s)
  }
  return out
}

// The brand words our own domains carry: the registrable labels of each domain
// we own, minus the public suffix ("revailo" out of revailo.com and
// revailo.ai). Only a label PRODUCT_RE would ACTUALLY match is worth blanking
// — "revailo" qualifies, "mail" and "dentalsitemaster" do not and are left
// alone, because blanking text nobody would have matched anyway only costs us
// evidence.
function brandLabelsOf(domains) {
  const out = new Set()
  for (const d of domains) {
    const parts = String(d).split('.').filter(Boolean)
    for (const label of parts.slice(0, -1)) {
      if (label.length >= 3 && PRODUCT_RE.test(label)) out.add(label.toLowerCase())
    }
  }
  return out
}

/**
 * Blank the parts of `text` that are OURS rather than the correspondent's.
 * Length-preserving (every removed character becomes a space) so offsets stay
 * comparable, and PURE — the caller says who "we" are.
 *
 * @param {string} text
 * @param {{addresses?:Iterable<string>, domains?:Iterable<string>}} [ours]
 * @returns {{text:string, hits:string[]}}  `hits` is what was blanked, in the
 *   text's own spelling, so a refusal can quote the thing it refused.
 */
export function stripOurOwnWords(text, ours = null) {
  const raw = String(text || '')
  const addresses = lowerSet(ours?.addresses)
  const domains = lowerSet(ours?.domains)
  if (!raw || (!addresses.size && !domains.size)) return { text: raw, hits: [] }

  const spans = []
  const hits = []
  // Text already inside a span is text we have already accounted for. Skipping
  // it keeps `hits` a list of the things we actually recognised — the domain in
  // "www.revailo.ai", not the domain AND the brand word inside it — so a
  // refusal quotes one identifiable thing rather than three views of it.
  const covered = (start, end) => spans.some(([s, e]) => start < e && end > s)
  const blank = (start, end, what) => {
    if (end <= start || covered(start, end)) return
    spans.push([start, end])
    const w = String(what || '').trim()
    if (w && !hits.includes(w)) hits.push(w)
  }

  // 1. our own addresses, wherever they appear.
  const ourAddrSpans = []
  for (const m of raw.matchAll(ADDR_RE)) {
    const addr = m[0].toLowerCase()
    if (!addresses.has(addr) && !domains.has(domainOf(addr))) continue
    ourAddrSpans.push([m.index, m.index + m[0].length])
    blank(m.index, m.index + m[0].length, m[0])
  }

  // 2. a bare mention of a domain we own — a footer URL ("www.revailo.ai"), a
  //    signature line, a "reply to sales@" instruction.
  //
  //    The leading group refuses a letter, digit, hyphen or "@" immediately
  //    before it: "@revailo.com" is the tail of an address and case 1's
  //    business, and "notrevailo.com" is somebody else's company. A DOT is
  //    deliberately allowed there, because "www." and "mail." are our own host
  //    with a label in front. The trailing lookahead refuses a longer name that
  //    merely starts the same way — "revailo.community" and a ".com.au" that is
  //    not ours — while still allowing the full stop at the end of a sentence.
  for (const d of domains) {
    const re = new RegExp(`(^|[^a-z0-9@-])(${escapeRe(d)})(?![a-z0-9-]|\\.[a-z0-9])`, 'gi')
    for (const m of raw.matchAll(re)) {
      const start = m.index + m[1].length
      blank(start, start + m[2].length, m[2])
    }
  }

  // 3. our brand name, but only where it stands inside our own signature.
  for (const label of brandLabelsOf(domains)) {
    const re = new RegExp(`\\b${escapeRe(label)}\\b`, 'gi')
    for (const m of raw.matchAll(re)) {
      const start = m.index
      const end = start + m[0].length
      const inSignature = ourAddrSpans.some(
        ([s, e]) => start < e + OUR_SIG_NEAR && end > s - OUR_SIG_NEAR,
      )
      if (inSignature) blank(start, end, m[0])
    }
  }

  if (!spans.length) return { text: raw, hits: [] }
  const chars = [...raw]
  for (const [start, end] of spans) {
    for (let i = start; i < end && i < chars.length; i += 1) chars[i] = ' '
  }
  return { text: chars.join(''), hits }
}

/**
 * Who "we" are, for one thread. The caller's set — built once per run from the
 * signed-in session, the team roster, the CRM owners and the mirror's own
 * derivation — is preferred, and the thread's OWN senders are folded in on top
 * so this function is still right when it is called with nothing: every
 * address we sent this thread from is ours by construction (readMailHistory
 * only records a sender it already judged ours).
 */
function ourIdentityFor(thread, ours) {
  const addresses = lowerSet(ours?.addresses)
  const domains = lowerSet(ours?.domains)
  const senders = thread?.senders
  const list = senders instanceof Set ? senders : Array.isArray(senders) ? senders : []
  for (const raw of list) {
    const a = String(raw || '').trim().toLowerCase()
    if (!a.includes('@')) continue
    addresses.add(a)
    const d = domainOf(a)
    // A sender's DOMAIN is only ours when it is not a shared consumer host:
    // one send from a gmail.com address must never make gmail.com "ours".
    if (d && !defaultSharedMailHost(d)) domains.add(d)
  }
  return { addresses, domains }
}

/**
 * Is this thread plausibly about our product / a sales conversation?
 *
 * @param {object} thread  a thread from readMailHistory().threads
 * @param {{addresses?:Iterable<string>, domains?:Iterable<string>}} [ours]
 *   who WE are — see stripOurOwnWords(). Optional: with nothing passed the
 *   thread's own senders still stand in, so this is never worse than it was.
 * @returns {{related:boolean, why:string, evidence:string}}
 */
export function classifyThread(thread, ours = null) {
  if (!thread) return { related: false, why: 'there is no thread to classify', evidence: '' }

  const subjects = Array.isArray(thread.subjects) ? thread.subjects : []
  const snippets = Array.isArray(thread.snippets) ? thread.snippets : []
  const text = [...subjects, ...snippets].join(' \n ').trim()

  if (isRobotAddress(thread.email)) {
    return {
      related: false,
      why: `${thread.email} is an automated address, not a person to follow up with`,
      evidence: '',
    }
  }

  if (!text) {
    // We hold the fact that mail moved and nothing about what it said. That is
    // not enough to put an email in front of a prospect over.
    return {
      related: false,
      why: 'we hold no subject or body text for this thread, so we cannot tell it is about the product — not clearly about the product',
      evidence: '',
    }
  }

  for (const rule of UNRELATED_RULES) {
    const hit = text.match(rule.re)
    if (hit) {
      return {
        related: false,
        why: `${rule.why} (“${String(hit[0]).slice(0, 40)}”)`,
        evidence: hit[0],
      }
    }
  }

  // THE PRODUCT TEST READS THEIR WORDS, NOT OUR LETTERHEAD. Our addresses, our
  // domains and our own signature block come out of the evidence first; see
  // stripOurOwnWords() for what is and is not removed, and why.
  const who = ourIdentityFor(thread, ours)
  const cleanSubjects = subjects.map((s) => stripOurOwnWords(s, who))
  const cleanSnippets = snippets.map((s) => stripOurOwnWords(s, who))
  const cleanText = [...cleanSubjects, ...cleanSnippets].map((c) => c.text).join(' \n ')

  // WHO WE ARE WRITING TO OUTRANKS WHAT THE MAIL SAYS. A colleague's thread can
  // be full of "software" and "pricing" and still not be a sale, so this runs
  // ahead of the product test — the same reason UNRELATED_RULES do.
  const internal = internalCounterpartWhy(thread.email, who)
  if (internal) return { related: false, why: internal.why, evidence: internal.evidence }

  // INTERNAL OPS, WEIGHED. Must also run ahead of the product test: the thread
  // that produced this bug carries our own product name in a forwarded footer
  // and the word "software" in half its snippets, and it is still a
  // conversation about a docking station. See internalOpsVerdict() for why this
  // one rule is scored instead of vetoing on the first word it recognises.
  const opsVerdict = internalOpsVerdict(text, cleanText)
  if (opsVerdict) {
    return { related: false, why: opsVerdict.why, evidence: opsVerdict.evidence }
  }

  const product = cleanText.match(PRODUCT_RE)
  if (product) {
    const where = cleanSubjects.some((s) => PRODUCT_RE.test(s.text)) ? 'subject' : 'body'
    const domain = thread.domain ? ` with ${thread.domain}` : ''
    return {
      related: true,
      why: `the thread${domain} is about the product — “${String(product[0]).slice(0, 40)}” appears in the ${where}`,
      evidence: product[0],
    }
  }

  // A product word that survives ONLY in our own footer is our letterhead, not
  // this thread's topic. Saying so out loud is the point: "we found the word
  // and refused it, here is where it came from" is checkable against the mail,
  // where a bare "not clearly about the product" would look like a miss.
  const ourHits = [...cleanSubjects, ...cleanSnippets].flatMap((c) => c.hits)
  const fromUs = text.match(PRODUCT_RE)
  if (fromUs && ourHits.length) {
    return {
      related: false,
      why: `the only product wording we hold is our own — “${String(fromUs[0]).slice(0, 40)}” comes from our own signature or address (“${String(ourHits[0]).slice(0, 40)}”) quoted in the thread, not from anything the other side wrote, so it is not clearly about the product`,
      evidence: ourHits[0],
    }
  }

  const weak = text.match(WEAK_RE)
  return {
    related: false,
    why: weak
      ? `only “${String(weak[0]).slice(0, 40)}” — no estimating, takeoff, demo, pricing or product wording anywhere in what we hold, so it is not clearly about the product`
      : 'nothing in the subject or body we hold names the product, estimating, a demo, pricing or a proposal — not clearly about the product',
    evidence: weak ? weak[0] : '',
  }
}

/**
 * The cadence numbers one thread contributes, in the shape agentRun's cohort
 * items already speak. Kept here so the arithmetic ("a bounce is not a touch",
 * "an auto-reply is not a reply") lives with the data that produced it.
 */
/**
 * The thread's real messages, newest first — what a card shows so a rep can
 * check the cadence against the mail itself. `limit` entries at most; every
 * field is copied from a mirrored HubSpot engagement.
 */
export function threadMessages(thread, limit = 6) {
  const list = Array.isArray(thread?.messages) ? thread.messages : []
  return list
    .filter((m) => m && m.at)
    .slice()
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
    .slice(0, Math.max(0, limit))
    .map((m) => ({
      dir: m.dir === 'in' ? 'in' : 'out',
      subject: String(m.subject || ''),
      snippet: String(m.snippet || ''),
      at: m.at,
      from: String(m.from || ''),
      ...(m.dir === 'out' && m.delivered === false ? { bounced: true } : {}),
      ...(m.dir === 'in' && m.auto ? { auto: true } : {}),
    }))
}

export function threadTouchSummary(thread) {
  if (!thread) return null
  return {
    email: thread.email,
    messages: threadMessages(thread),
    touchDays: thread.touches instanceof Set ? [...thread.touches] : [],
    touchCount: thread.touches instanceof Set ? thread.touches.size : 0,
    lastOutboundAt: thread.lastOutboundAt || '',
    lastInboundAt: thread.lastInboundAt || '',
    bouncedCount: thread.bouncedCount || 0,
    outboundCount: thread.outboundCount || 0,
    companyIds: [...(thread.companyIds || [])],
    lastSubject: thread.lastOutbound?.subject || '',
    lastSnippet: thread.lastOutbound?.snippet || '',
    replySubject: thread.lastInbound?.subject || '',
    replySnippet: thread.lastInbound?.snippet || '',
    replyAt: thread.lastInbound?.at || '',
  }
}
